import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardCheck, Ruler, UserPlus } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tailoring-crm/ui';

const STEPS = [
  { key: 'customer', icon: UserPlus },
  { key: 'measurement', icon: Ruler },
  { key: 'order', icon: ClipboardCheck },
] as const;

/**
 * The greeting a new shop gets on its first look at Home: what the next two minutes hold, and
 * the choice to start now, later, or never. Closing it any other way is "later" — the Home card
 * still offers the tour.
 */
export function TourWelcome({
  shopName,
  onStart,
  onSkip,
  onLater,
}: {
  shopName: string;
  onStart: () => void;
  onSkip: () => void;
  onLater: () => void;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <Dialog open onOpenChange={(open) => !open && onLater()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('onboarding.spotlight.welcome.title', { shop: shopName })}</DialogTitle>
          <DialogDescription>{t('onboarding.spotlight.welcome.description')}</DialogDescription>
        </DialogHeader>

        <ol className="flex flex-col gap-2">
          {STEPS.map(({ key, icon: Icon }, index) => (
            <li
              key={key}
              // Each line arrives a beat after the last, so the eye reads them in order.
              style={{ animationDelay: `${150 + index * 120}ms` }}
              className="flex items-center gap-3 rounded-lg bg-muted px-3 py-2.5 text-sm animate-in fade-in-0 slide-in-from-left-4 fill-mode-both duration-500 motion-reduce:animate-none"
            >
              <span
                aria-hidden="true"
                className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground"
              >
                <Icon className="size-4" />
              </span>
              <span>
                <span className="font-semibold">{t(`onboarding.tour.${key}`)}</span>
                <span className="block text-muted-foreground">
                  {t(`onboarding.spotlight.welcome.${key}`)}
                </span>
              </span>
            </li>
          ))}
        </ol>

        <DialogFooter>
          <Button variant="ghost" onClick={onSkip}>
            {t('onboarding.spotlight.skip')}
          </Button>
          <Button variant="outline" onClick={onLater}>
            {t('onboarding.spotlight.later')}
          </Button>
          <Button onClick={onStart} autoFocus>
            {t('onboarding.spotlight.welcome.start')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
